import { find } from 'lodash';
import { action, makeObservable, observable } from 'mobx';
import { CompressorParameter } from './common';
import { compressorEditableFields, CompressorParameterSave } from './constants';
import { convertPressureToImperial, convertPressureToMetric, isEmptyField } from './utils';
import { EngineTab } from '@/constants';
import { t } from 'i18next';
import { UnitMeasurementSystem } from '@/constants/enums';

export class CompressorParametersConfiguration {
  @observable compressorParameters: CompressorParameter[];
  @observable oldValues: Partial<CompressorParametersConfiguration>;
  @observable isLoading = false;
  constructor(compressorsConfiguration?, compressors?, tvalue?, unitMeasurementSystem?) {
    makeObservable(this);
    this.isLoading = true;

    compressorsConfiguration = compressorsConfiguration ?? [];
    let compressorParameters = [];
    if (Array.isArray(compressorsConfiguration)) {
      compressorParameters = compressorsConfiguration;
    } else {
      compressorParameters = compressorsConfiguration?.compressorParameters;
    }
    this.compressorParameters = (compressors ?? []).map(compressor => {
      const matchedConfig = compressorParameters?.find(config => config.deviceId === compressor.id);

      return matchedConfig
        ? {
            ...compressor,
            ...matchedConfig,
            maxFlow: compressor.attributes?.maxFlow,
            maxLoadAmps: compressor.attributes?.maxLoadAmps,
            fiveLoop: compressor.attributes?.fiveLoop,
          }
        : {
            compressor,
            deviceId: compressor.id,
            compressorName: compressor.name,
            compressorNumber: compressor.attributes?.compressorNumber,
            maxFlow: compressor.attributes?.maxFlow,
            maxLoadAmps: compressor.attributes?.maxLoadAmps,
            fiveLoop: compressor.attributes?.fiveLoop,
          };
    });

    this.compressorParameters = CompressorParameter.convertCompressorParametersToUnitMeasurementSystem(
      this.compressorParameters,
      tvalue,
      unitMeasurementSystem,
      convertPressureToImperial,
    ).sort((a, b) => a.compressorNumber - b.compressorNumber);
    if (!unitMeasurementSystem) return;
    this.oldValues = {};
    this.oldValues.compressorParameters = this.compressorParameters;
    this.isLoading = false;
  }

  @action setCompressorParameterField(key, field, value) {
    this.compressorParameters = this.compressorParameters?.map(parameter => {
      return parameter?.key === key ? new CompressorParameter({ ...parameter, [field]: value }) : parameter;
    });
  }

  hasChanges() {
    return this.compressorParameters?.some(parameter => {
      const oldParameter = find(this.oldValues?.compressorParameters, { deviceId: parameter.deviceId });
      return compressorEditableFields.some(field => Number(parameter?.[field]) !== Number(oldParameter?.[field]));
    });
  }
  @action cancelChanges() {
    this.compressorParameters = this.oldValues?.compressorParameters.map(param => new CompressorParameter(param)) ?? [];
  }

  getOldStationParameterByKey(key) {
    return find(this.oldValues?.compressorParameters, { key });
  }

  getValuesToSave(tvalue, unitMeasurementSystem): CompressorParameterSave[] {
    const convertPressure =
      unitMeasurementSystem === UnitMeasurementSystem.Imperial ? convertPressureToMetric : value => value;
    return (
      this.compressorParameters?.map(param => ({
        deviceId: param.deviceId,
        minTarget: param.minTarget ? Number(convertPressure(param.minTarget, tvalue)) : null,
        maxTarget: param.maxTarget ? Number(convertPressure(param.maxTarget, tvalue)) : null,
        stopDelayMinutes: param.stopDelayMinutes ? Number(param.stopDelayMinutes) : param.stopDelayMinutes,
        priority: param.priority ? Number(param.priority) : param.priority,
      })) ?? []
    );
  }

  validate() {
    const validationMessages = [];
    const parameters = this.compressorParameters.sort(
      (a, b) => Number(a.compressorNumber) - Number(b.compressorNumber),
    );

    const validateRequired = (parameter, field) => {
      if (isEmptyField(parameter[field])) {
        validationMessages.push({
          key: parameter?.key,
          field,
          message: t('required'),
          tArgs: { [field]: parameter?.[field] },
          tab: EngineTab.CompressorParameters,
        });
      }
    };

    const requiredFieldsToValidate = ['priority', 'maxTarget', 'minTarget', 'stopDelayMinutes'];

    for (const param of parameters ?? []) {
      for (const field of requiredFieldsToValidate) {
        validateRequired(param, field);
        if (validationMessages.length > 0) return validationMessages;
      }
      if (Number(param.minTarget) >= Number(param.maxTarget)) {
        validationMessages.push({
          key: param.key,
          field: 'minTarget',
          message: t('validationForMinTargetLessThanMaxTarget'),
          tArgs: { minValue: param.minTarget, maxValue: param.maxTarget },
          tab: EngineTab.CompressorParameters,
        });
        return validationMessages;
      }
    }
    return validationMessages;
  }
}
